// Plugin pour filtrer le bruit console non critique (Cloudflare, Pinia devtools, Suspense, etc.)
// S'exécute avant tout pour intercepter les messages dès le chargement

export default defineNuxtPlugin({
  name: 'console-noise-filter',
  enforce: 'pre',
  setup() {
  const isDevConsoleNoise = (message: string): boolean => {
    if (!message) return false

    const noisePatterns = [
      /<Suspense>\s+is an experimental feature/i,
      /store installed/i,
      /store disposed/i,
      /^🍍\s/,
      /Nuxt DevTools/i,
    ]

    return noisePatterns.some((pattern) => pattern.test(message))
  }

  // Fonction pour vérifier si une erreur est liée à Cloudflare et non critique
  const isCloudflareNonCriticalError = (message: string, source?: string, url?: string): boolean => {
    if (!message || typeof message !== 'string') {
      // Vérifier aussi l'URL si fournie
      if (url && typeof url === 'string') {
        message = url
      } else {
        return false
      }
    }
    
    const cloudflarePatterns = [
      'challenges.cloudflare.com',
      'challenge-platform',
      'Private Access Token',
      'preload but not used',
      'script-src.*default-src',
      'cdn-cgi/challenge-platform',
      '/pat/', // Private Access Token
      '/cmg/', // Challenge platform
      '/normal\\?lang=auto',
      '401.*Unauthorized.*challenges.cloudflare.com',
      'challenge-platform/h/b',
      'dzHcRmk1J7lWICX', // Pattern spécifique des erreurs PAT
      'Note that.*script-src.*was not explicitly set'
    ]
    
    const fullMessage = [message, source, url].filter(Boolean).join(' ')
    
    return cloudflarePatterns.some(pattern => {
      try {
        const regex = new RegExp(pattern, 'i')
        return regex.test(fullMessage)
      } catch {
        // Si le pattern regex échoue, utiliser includes
        return fullMessage.toLowerCase().includes(pattern.toLowerCase())
      }
    })
  }

  const shouldSuppressDevNoise = (message: string): boolean => isDevConsoleNoise(message)

  const shouldSuppressWarnOrInfo = (message: string): boolean =>
    shouldSuppressDevNoise(message) || isCloudflareNonCriticalError(message)

  // Intercepter console.error, console.warn, console.log et console.info
  const originalError = console.error.bind(console)
  const originalWarn = console.warn.bind(console)
  const originalLog = console.log.bind(console)
  const originalInfo = console.info.bind(console)

  // Conversion robuste en chaîne, sans lever d'erreur
  const safeStringify = (arg: any): string => {
    if (typeof arg === 'string') return arg

    if (arg instanceof Error) {
      return arg.message || arg.toString()
    }

    try {
      const json = JSON.stringify(arg)
      if (json !== undefined) {
        return json
      }
    } catch {
      // on ignore et on tombe sur le fallback
    }

    try {
      return String(arg)
    } catch {
      return '[objet non sérialisable]'
    }
  }

  console.error = (...args: any[]) => {
    const message = args.map(safeStringify).join(' ')

    if (isCloudflareNonCriticalError(message)) {
      return // Ignorer silencieusement
    }
    originalError(...args)
  }

  console.warn = (...args: any[]) => {
    const message = args.map(safeStringify).join(' ')

    if (shouldSuppressWarnOrInfo(message)) {
      return
    }
    originalWarn(...args)
  }

  console.log = (...args: any[]) => {
    const message = args.map(safeStringify).join(' ')

    if (
      shouldSuppressDevNoise(message) ||
      (isCloudflareNonCriticalError(message) && message.includes('challenges.cloudflare.com'))
    ) {
      return
    }

    originalLog(...args)
  }

  console.info = (...args: any[]) => {
    const message = args.map(safeStringify).join(' ')

    if (shouldSuppressWarnOrInfo(message)) {
      return
    }
    originalInfo(...args)
  }

  // Intercepter les erreurs JavaScript non gérées (capture phase)
  window.addEventListener('error', (event) => {
    const message = event.message || ''
    const source = event.filename || ''
    const url = (event.target as any)?.src || (event.target as any)?.href || ''
    
    if (
      isCloudflareNonCriticalError(message, source, url) ||
      source.includes('challenges.cloudflare.com') ||
      url.includes('challenges.cloudflare.com')
    ) {
      event.preventDefault()
      event.stopPropagation()
      return false
    }
  }, true) // Utiliser capture phase pour intercepter avant les autres handlers

  // Intercepter les erreurs de promesses non gérées
  window.addEventListener('unhandledrejection', (event) => {
    const message = safeStringify(event.reason ?? '')

    if (isCloudflareNonCriticalError(message)) {
      event.preventDefault()
      event.stopPropagation()
      return false
    }
  }, true)

  // Note: Les erreurs 401 dans l'onglet Network ne peuvent pas être masquées
  // car elles sont affichées directement par le navigateur.
  // Cependant, les messages d'erreur dans la console sont maintenant filtrés.
  },
})

